import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useView } from '../contexts/ViewContext';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  AlertCircle,
  CheckCircle,
  Clock,
  FileText,
  Plus,
  Search,
  XCircle,
  RefreshCw,
  Filter,
} from 'lucide-react';
import { barrattAPI, type BarrattDeliveryAppeal, type DeliveryAppealStats } from '../lib/api';
import { useFormatters } from '../contexts/PreferencesContext';
type DeliveryAppeal = BarrattDeliveryAppeal;

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  submitted: 'bg-blue-100 text-blue-800',
  under_review: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-500',
};

const statusIcons: Record<string, React.ReactNode> = {
  draft: <FileText className="h-4 w-4" />,
  submitted: <Clock className="h-4 w-4" />,
  under_review: <RefreshCw className="h-4 w-4" />,
  approved: <CheckCircle className="h-4 w-4" />,
  rejected: <XCircle className="h-4 w-4" />,
  cancelled: <AlertCircle className="h-4 w-4" />,
};

const DeliveryAppeals: React.FC = () => {
  const navigate = useNavigate();
  // CR-6307: delivery dates through the preference-aware formatter (US format + no day-shift).
  const { formatDate } = useFormatters();
  const { getViewQueryParams, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId, isLoading: viewContextLoading } = useView();
  const [appeals, setAppeals] = useState<DeliveryAppeal[]>([]);
  const [stats, setStats] = useState<DeliveryAppealStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedAppeal, setSelectedAppeal] = useState<DeliveryAppeal | null>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject'>('approve');
  const [reviewNotes, setReviewNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    if (viewContextLoading) return;
    loadData();
  }, [viewContextLoading, statusFilter, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const viewParams = getViewQueryParams();
      const params: Record<string, string | number | undefined> = { ...viewParams };
      if (statusFilter !== 'all') {
        params['status'] = statusFilter;
      }

      const [appealsRes, statsRes] = await Promise.all([
        barrattAPI.getDeliveryAppeals(params as Record<string, string | number>),
        barrattAPI.getDeliveryAppealStats(),
      ]);

      setAppeals(appealsRes.data.results || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load delivery appeals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async () => {
    if (!selectedAppeal) return;

    try {
      await barrattAPI.reviewDeliveryAppeal(selectedAppeal.id, {
        action: reviewAction,
        reviewer_notes: reviewNotes,
        rejection_reason: reviewAction === 'reject' ? rejectionReason : undefined,
      });
      setReviewDialogOpen(false);
      setSelectedAppeal(null);
      setReviewNotes('');
      setRejectionReason('');
      loadData();
    } catch (error) {
      console.error('Failed to review appeal:', error);
    }
  };

  const filteredAppeals = appeals.filter((appeal) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      appeal.appeal_number.toLowerCase().includes(search) ||
      appeal.po_number?.toLowerCase().includes(search) ||
      appeal.reason.toLowerCase().includes(search)
    );
  });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Delivery Appeals</h1>
          <p className="text-gray-500">Manage and review delivery dispute appeals</p>
        </div>
        <Button onClick={() => navigate('/delivery-appeals/new')}>
          <Plus className="h-4 w-4 mr-2" />
          New Appeal
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{stats.total}</div>
              <div className="text-sm text-gray-500">Total Appeals</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-blue-600">
                {stats?.['by_status']?.["submitted"] || 0}
              </div>
              <div className="text-sm text-gray-500">Pending Review</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-yellow-600">
                {stats?.['by_status']?.["under_review"] || 0}
              </div>
              <div className="text-sm text-gray-500">Under Review</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-600">
                {stats?.['by_status']?.["approved"] || 0}
              </div>
              <div className="text-sm text-gray-500">Approved</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-red-600">
                {stats?.['by_status']?.["rejected"] || 0}
              </div>
              <div className="text-sm text-gray-500">Rejected</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-gray-600">
                {stats?.['by_status']?.["draft"] || 0}
              </div>
              <div className="text-sm text-gray-500">Draft</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search appeals..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="under_review">Under Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Appeals Table */}
      <Card>
        <CardHeader>
          <CardTitle>Appeals</CardTitle>
          <CardDescription>
            {filteredAppeals.length} appeals found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : filteredAppeals.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No delivery appeals found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Appeal #</TableHead>
                  <TableHead>PO #</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Original Date</TableHead>
                  <TableHead>Actual Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAppeals.map((appeal) => (
                  <TableRow key={appeal.id}>
                    <TableCell className="font-medium">
                      {appeal.appeal_number}
                    </TableCell>
                    <TableCell>{appeal.po_number || '-'}</TableCell>
                    <TableCell>{appeal.appeal_type_display}</TableCell>
                    <TableCell>
                      {appeal.original_delivery_date ? formatDate(appeal.original_delivery_date) : '-'}
                    </TableCell>
                    <TableCell>
                      {appeal.actual_delivery_date
                        ? formatDate(appeal.actual_delivery_date)
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[appeal.status]}>
                        <span className="flex items-center gap-1">
                          {statusIcons[appeal.status]}
                          {appeal.status_display}
                        </span>
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {formatDate(appeal.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/delivery-appeals/${appeal.id}`)}
                        >
                          View
                        </Button>
                        {(appeal.status === 'submitted' || appeal.status === 'under_review') && (
                          <Button
                            size="sm"
                            onClick={() => {
                              setSelectedAppeal(appeal);
                              setReviewDialogOpen(true);
                            }}
                          >
                            Review
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review Appeal</DialogTitle>
            <DialogDescription>
              Review and make a decision on appeal {selectedAppeal?.appeal_number}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Decision</Label>
              <Select
                value={reviewAction}
                onValueChange={(v) => setReviewAction(v as 'approve' | 'reject')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="approve">Approve</SelectItem>
                  <SelectItem value="reject">Reject</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Review Notes</Label>
              <Textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Add any notes about your decision..."
              />
            </div>
            {reviewAction === 'reject' && (
              <div>
                <Label>Rejection Reason *</Label>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Explain why the appeal is being rejected..."
                  required
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleReview}
              disabled={reviewAction === 'reject' && !rejectionReason}
              className={reviewAction === 'approve' ? 'bg-green-600' : 'bg-red-600'}
            >
              {reviewAction === 'approve' ? 'Approve Appeal' : 'Reject Appeal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeliveryAppeals;
